import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { SectionTab, SectionTabs } from '../components/ui/section-tabs';
import { useToast } from '../components/ui/use-toast';
import { Pencil, UserPlus, UserMinus, Share2 } from 'lucide-react';
import { usersService } from '../services/users';
import { localStorageService } from '../services/localStorage';
import { usePushNudgeDialog } from '../components/push/PushNudgeDialog';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import UserSendsGrid from '../components/sends/UserSendsGrid';
import UserCreatedClimbsGrid from '../components/profile/UserCreatedClimbsGrid';
import FollowersList from '../components/FollowersList';
import PointsDisplay from '../components/profile/PointsDisplay';
import BadgesCarousel from '../components/profile/BadgesCarousel';
import UserInsights from '../components/profile/insights/UserInsights';
import ProfileQrCode from '../components/profile/ProfileQrCode';
import ScannedProfileDialog from '../components/profile/ScannedProfileDialog';
import type { ProfileOrigin } from '../components/profile/profile-origin';
import type { UserQrCodeVerification } from '../services/users';
import type { User } from '../types/user';

export default function UserProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatarEnlarged, setAvatarEnlarged] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const pushNudge = usePushNudgeDialog('follow_user');
  // A follow from the scanned-code dialog asks once that dialog has closed:
  // two dialogs in flight leave Radix's scroll lock on the body.
  const followedFromScan = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [scanned, setScanned] = useState<UserQrCodeVerification | null>(null);
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);
  const [scannedDialogOpen, setScannedDialogOpen] = useState(false);

  const scannedCode = searchParams.get('code');
  const USER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const isOwnProfile = currentUser?.slug === slug;
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Restore last selected tab from localStorage
    const savedTab = localStorage.getItem(`userProfileTab_${slug}`);
    return savedTab || 'insights';
  });

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    localStorage.setItem(`userProfileTab_${slug}`, value);
  };

  useBreadcrumb(
    () => ({ onBack: () => navigate(-1), segments: user ? [{ label: user.full_name || user.username || '' }] : [], loading }),
    [user?.full_name, user?.username, loading]
  );

  const fetchUser = useCallback(async () => {
    if (!slug) return;

    const cacheKey = `user_profile_${slug}`;
    if (!isOwnProfile) {
      const cached = localStorageService.get<User>(cacheKey);
      if (cached) {
        setUser(cached);
        setIsFollowing(cached.is_following || false);
        setLoading(false);
        // Revalidate in background so points/badges stay fresh
      }
    }

    try {
      if (!isOwnProfile) {
        // If we already showed cached data, don't show spinner again
        const hasCached = !!localStorageService.get<User>(cacheKey);
        if (!hasCached) setLoading(true);
      } else {
        setLoading(true);
      }
      const userData = await usersService.getUserBySlug(slug);
      setUser(userData);
      setIsFollowing(userData.is_following || false);
      if (!isOwnProfile) {
        localStorageService.set(cacheKey, userData, USER_CACHE_TTL);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setLoading(false);
      // Show toast error outside of useCallback to avoid dependency issues
      setTimeout(() => {
        toast({
          title: t('userProfile.failedToLoad'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]); // toast and t are stable and don't need to be in deps

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // A scanned QR lands here with a signed code attached. Let the backend vouch
  // for it, then drop it from the URL so a valid code is not kept in history or
  // passed on by sharing the link.
  useEffect(() => {
    if (!scannedCode || !slug || !currentUser) return;

    let cancelled = false;

    const verify = async () => {
      let verification: UserQrCodeVerification | null = null;
      try {
        verification = await usersService.verifyQrCode(slug, scannedCode);
      } catch (error) {
        console.error('Failed to verify QR code:', error);
      }
      if (cancelled) return;

      if (verification) {
        setScanned(verification);
        // Kept in state because registering re-submits it, while the URL is
        // about to be stripped of the code below.
        setVerifiedCode(scannedCode);
        setScannedDialogOpen(true);
      } else {
        toast({
          title: t('userProfile.scanned.invalidTitle'),
          description: t('userProfile.scanned.invalidDescription'),
          variant: 'destructive',
        });
      }

      setSearchParams(
        (params) => {
          params.delete('code');
          return params;
        },
        { replace: true }
      );
    };

    verify();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedCode, slug, currentUser?.id]); // toast and t are stable

  if (!slug) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Skeleton className="h-32 w-32 rounded-full" />
              <div className="space-y-2 text-center">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-6 w-32" />
              </div>
              <div className="flex space-x-2">
                <Skeleton className="h-10 w-20" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">
              {t('userProfile.notFound')}
            </h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  // What a climb opened from this profile's grids leads its breadcrumb with.
  const profileOrigin: ProfileOrigin = {
    slug,
    name: user.full_name || user.username || '',
    avatarUrl: user.avatar_small_url,
  };

  const handleAvatarClick = () => {
    if (user.avatar_url) {
      setAvatarEnlarged(true);
    }
  };

  const closeEnlargedAvatar = () => {
    setAvatarEnlarged(false);
  };

  const handleToggleFollow = async () => {
    if (!currentUser || !slug) {
      return;
    }

    setFollowLoading(true);
    try {
      const cacheKey = `user_profile_${slug}`;
      if (isFollowing) {
        await usersService.unfollowUser(slug);
        setIsFollowing(false);
        if (user) localStorageService.set(cacheKey, { ...user, is_following: false }, USER_CACHE_TTL);
        toast({
          title: t('userProfile.unfollowSuccess'),
          description: t('userProfile.unfollowSuccessDescription', { name: user?.full_name || user?.username }),
        });
      } else {
        await usersService.followUser(slug);
        setIsFollowing(true);
        if (user) localStorageService.set(cacheKey, { ...user, is_following: true }, USER_CACHE_TTL);
        toast({
          title: t('userProfile.followSuccess'),
          description: t('userProfile.followSuccessDescription', { name: user?.full_name || user?.username }),
        });
        pushNudge.maybeOpen({ name: user?.full_name || user?.username });
      }
    } catch (error) {
      // apiClient già mostra il toast di errore automaticamente
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: user?.full_name || user?.username || t('userProfile.share'),
          text: t('userProfile.shareText', { name: user?.full_name || user?.username }),
          url: window.location.href,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: t('userProfile.linkCopied'),
          description: t('userProfile.linkCopiedDescription'),
          duration: 3000,
        });
      }
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
        toast({
          title: t('errors.shareFailed'),
          description: t('errors.shareFailedDescription'),
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              {/* Profile Picture */}
              <div
                className={user.avatar_medium_url ? "cursor-pointer" : ""}
                onClick={handleAvatarClick}
              >
                <Avatar className="h-32 w-32 ring-4 ring-background shadow-lg">
                  <AvatarImage
                    src={user.avatar_medium_url}
                    alt={t('userProfile.profilePicture')}
                  />
                  <AvatarFallback className="text-2xl font-bold">
                    {user.username?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* User Info */}
              <div className="space-y-2 text-center">
                <h1 className="text-2xl font-bold">{user.full_name}</h1>
                <p className="text-lg text-muted-foreground">@{user.username}</p>
              </div>

              {/* Points */}
              <PointsDisplay
                totalPoints={user.total_points ?? 0}
                pointsByCategory={user.points_by_category}
              />

              {/* Follow Button - Only show if not own profile and logged in */}
              <div className="flex items-center gap-2">
                {!isOwnProfile && currentUser && (
                  <Button
                    className="px-8"
                    variant={isFollowing ? "outline" : "default"}
                    onClick={handleToggleFollow}
                    disabled={followLoading}
                  >
                    {followLoading ? (
                      t('userProfile.loading')
                    ) : isFollowing ? (
                      <>
                        <UserMinus className="h-4 w-4 mr-2" />
                        {t('userProfile.unfollow')}
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t('userProfile.follow')}
                      </>
                    )}
                  </Button>
                )}
                {isOwnProfile && currentUser && (
                  <>
                    <Button asChild className="px-8" variant="default">
                      <Link to="/profile">
                        <Pencil className="h-4 w-4 mr-2" />
                        {t('userProfile.edit')}
                      </Link>
                    </Button>
                    <ProfileQrCode userSlug={slug} />
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleShare}
                  title={t('userProfile.share')}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Followers */}
              <FollowersList
                followers={user.followers || []}
                totalCount={user.followers_count}
                followable={{ kind: 'users', slug: user.slug }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Badges, outside the card: a strip that scrolls sideways past the
            gutters, the way the sends do below. */}
        {user.badges && user.badges.length > 0 && (
          <div className="-mx-4 sm:-mx-6" data-testid="user-badges-bleed">
            <BadgesCarousel badges={user.badges} />
          </div>
        )}

        {/* The profile's sections, laid out as the gym page's climbs are (see
            ClimbsList): the same tab strip, no card around it, the section
            straight under it. */}
        <div className="space-y-3" data-testid="user-profile-tabs">
          <SectionTabs>
            <SectionTab active={activeTab === 'insights'} onClick={() => handleTabChange('insights')}>
              {t('userProfile.insightsTab')}
            </SectionTab>
            <SectionTab active={activeTab === 'sends'} onClick={() => handleTabChange('sends')}>
              {t('userProfile.sends')}
            </SectionTab>
            <SectionTab active={activeTab === 'created'} onClick={() => handleTabChange('created')}>
              {t('userProfile.createdClimbs')}
            </SectionTab>
          </SectionTabs>

          {activeTab === 'insights' && <UserInsights userSlug={slug} />}
        </div>
      </div>

      {/* The sends and the climbs put up, outside the page's column: a wall
          of photos wants the whole width, so it spans the page the way the gym
          page's cover does — the container edge to edge, past the gutters. */}
      {activeTab === 'sends' && (
        <div className="mt-3 -mx-4 sm:-mx-6" data-testid="user-sends-bleed">
          <UserSendsGrid userSlug={slug} profile={profileOrigin} />
        </div>
      )}

      {activeTab === 'created' && (
        <div className="mt-3 -mx-4 sm:-mx-6" data-testid="user-created-climbs-bleed">
          <UserCreatedClimbsGrid userSlug={slug} profile={profileOrigin} />
        </div>
      )}

      {/* Landed here from a scanned QR code */}
      {scanned && verifiedCode && (
        <ScannedProfileDialog
          open={scannedDialogOpen}
          onOpenChange={(open) => {
            setScannedDialogOpen(open);
            if (!open && followedFromScan.current) {
              followedFromScan.current = false;
              window.setTimeout(() => pushNudge.maybeOpen({ name: scanned.full_name }), 350);
            }
          }}
          scanned={scanned}
          // Admin only: registering a member goes through the members API,
          // which no other staff role may call.
          staffGyms={(currentUser?.staff || []).filter(
            (s) => s.status === 'confirmed' && s.staff_roles.includes('admin')
          )}
          scannedCode={verifiedCode}
          alreadyFollowing={isFollowing}
          onFollowed={() => {
            setIsFollowing(true);
            followedFromScan.current = true;
          }}
        />
      )}

      {/* Enlarged Avatar Modal */}
      {avatarEnlarged && user.avatar_url && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeEnlargedAvatar}
        >
          <div className="max-w-lg max-h-[80vh] relative">
            <img
              src={user.avatar_url}
              alt={t('userProfile.profilePicture')}
              className="w-full h-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {pushNudge.dialog}
    </>
  );
}
